import { AlertResponse, Subscription } from '@eagle/core-data-types';
import { Paper, styled, TableCell, Typography } from '@ethings/ui-toolkit';
import { Tooltip, useTheme } from '@mui/material';
import { FC, type JSX } from 'react';
import { useHref } from 'react-router-dom';
import { useNotableEvents } from '../../hooks/use-notable-events';
import { getAlertTypeStyle, getEventHint, getEventLabelWithFlag, getFeatureLabelWithFlag, getFeatureType } from '../../util';
import { useBoolFlag } from '../flags';
import { ResultsTableRow } from '../search-results-table';
import { SubscriptionUserRecipientDataType } from './subscriptions.types';
import { COLUMN_WIDTH, getEntityData, RecipientDisplay, SourceDisplay } from './subscriptions.util';

interface SubscriptionsTableRowProps {
  disableLink?: boolean;
  userRecipientDataType: SubscriptionUserRecipientDataType;
  subscription: Subscription;
  subscriptionDetailPath: string;
  'data-testid'?: string;
}

interface StyledPaperProps {
  alertTypeId: string;
  featureTypeId: string;
  feature: string;
  hint?: true;
}

const StyledPaper = styled(Paper) <StyledPaperProps>`
  ${({ alertTypeId, featureTypeId, feature, hint }) => ({
    ...getAlertTypeStyle({ alertTypeId, featureTypeId, feature } as AlertResponse, useTheme(), useNotableEvents()),
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    ...(hint ? { padding: '0.5rem' } : {}),
  })}
`;

const StyledTableCell = styled(TableCell)();

export const SubscriptionsTableRow: FC<SubscriptionsTableRowProps> = ({ subscription, disableLink, subscriptionDetailPath, userRecipientDataType, ...props }) => {
  const href = useHref(subscriptionDetailPath);
  const { _id, alertFilters, recipient } = subscription;
  const { alertTypeId, feature } = alertFilters;
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const isHintEnabled = useBoolFlag('portals-event-translation-hints-feature');
  const featureTypeId = getFeatureType(alertTypeId);

  const renderContent = (): JSX.Element => <>
    <StyledTableCell component="div" sx={{ width: COLUMN_WIDTH }} data-testid="subscription-recipient">
      <RecipientDisplay userDataType={userRecipientDataType} recipient={recipient} />
    </StyledTableCell>

    <StyledTableCell component="div" sx={{ width: COLUMN_WIDTH }} data-testid="subscription-entity">
      <SourceDisplay entityData={getEntityData(alertFilters)} />
    </StyledTableCell>

    <StyledTableCell component="div" sx={{ width: COLUMN_WIDTH }}>
      <Typography data-testid="subscription-feature">{feature ? getFeatureLabelWithFlag(feature, flagUseEventV3) : feature}</Typography>
    </StyledTableCell>

    <StyledTableCell component="div" sx={{ width: COLUMN_WIDTH }}>
      {alertTypeId && featureTypeId && feature ?
        isHintEnabled ? (
          <Tooltip title={getEventHint(feature, alertTypeId)} placement="bottom" arrow>
            <StyledPaper
              data-testid="subscription-alert-description"
              elevation={0}
              alertTypeId={alertTypeId}
              featureTypeId={featureTypeId}
              feature={feature}
              hint={true}
            >
              {getEventLabelWithFlag(featureTypeId, alertTypeId, feature, flagUseEventV3)}
            </StyledPaper>
          </Tooltip>
        ) : (
          <StyledPaper
            data-testid="subscription-alert-description"
            elevation={0}
            alertTypeId={alertTypeId}
            featureTypeId={featureTypeId}
            feature={feature}
          >
            {getEventLabelWithFlag(featureTypeId, alertTypeId, feature, flagUseEventV3)}
          </StyledPaper>
        ) : null
      }
    </StyledTableCell>
  </>;

  return (
    <ResultsTableRow
      data-testid={props['data-testid']}
      content={renderContent()}
      deleted={!!subscription.finish}
      href={disableLink ? undefined : `${href}/${_id}`}
    />
  );
};
